import { Carousel } from "antd";
import { useEffect, useRef, useState } from 'react';
import tongueCloud from '../../images/tongue-cloud.png';
import tonguePoint from '../../images/tongue-point.png';

// 工具函数：计算叉积，用于判断三点之间的相对位置关系
const crossProduct = (a: number[], b: number[], c: number[]) => {
    return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
};

// Graham扫描算法，用于计算点集的凸包
const grahamScan = (points: number[][]) => {
    if (points.length < 3) return points;

    let minYIndex = 0;
    for (let i = 1; i < points.length; i++) {
        if (
            points[i][1] < points[minYIndex][1] ||
            (points[i][1] === points[minYIndex][1] && points[i][0] < points[minYIndex][0])
        ) {
            minYIndex = i;
        }
    }

    const pivot = points[minYIndex];
    const remaining = points.filter((_, index) => index !== minYIndex);

    remaining.sort((a, b) => {
        const cross = crossProduct(pivot, a, b);
        if (cross === 0) {
            return (
                (a[0] - pivot[0]) ** 2 + (a[1] - pivot[1]) ** 2 -
                (b[0] - pivot[0]) ** 2 - (b[1] - pivot[1]) ** 2
            );
        }
        return cross > 0 ? -1 : 1;
    });

    const stack: number[][] = [pivot, remaining[0]];
    for (let i = 1; i < remaining.length; i++) {
        while (
            stack.length > 1 &&
            crossProduct(
                stack[stack.length - 2],
                stack[stack.length - 1],
                remaining[i]
            ) <= 0
        ) {
            stack.pop();
        }
        stack.push(remaining[i]);
    }
    return stack;
};

const TongueCheck = ({ visible, onCancel, record, tongueFeatures }: any) => {
    const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
    const carouselRef = useRef<any>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const getValue = () => {
        if (record?.resultData.checkResult.relativeComparison.length > 2) {
            return [
                {
                    index: 0,
                    title: '舌色',
                    value: tongueFeatures?.tongueColor[0].contentName
                },
                {
                    index: 1,
                    title: '舌质',
                    value: tongueFeatures?.coatingColor[0].contentName
                },
                {
                    index: 2,
                    title: '舌形',
                    value: tongueFeatures?.tongueShape
                        ?.map((item: any) => item.contentName)
                        ?.filter(Boolean)
                        ?.join(', ')
                },
                // {
                //     index: 3,
                //     title: '舌象阴影',
                //     value: '如图所示'
                // }
            ];
        } else {
            return [
                {
                    index: 0,
                    title: '舌色',
                    value: tongueFeatures?.tongueColor[0].contentName
                },
                {
                    index: 1,
                    title: '舌形',
                    value: tongueFeatures?.tongueShape
                        ?.map((item: any) => item.contentName)
                        ?.filter(Boolean)
                        ?.join(', ')
                }
            ];
        }
    };

    const handleNext = () => {
        const totalSlides = getValue().length;
        if (activeIndex < totalSlides - 1) {
            // 如果不是最后一张图，则切换到下一张
            setActiveIndex(prev => prev + 1);
            // 手动触发carousel的切换
            if (carouselRef.current) {
                carouselRef.current.goTo(activeIndex + 1);
            }
        } else {
            // 如果是最后一张图，则关闭弹窗
            onCancel();
        }
    };

    // 绘制函数：在canvas上绘制舌象图片和标记点
    const drawOnCanvas = (
        canvas: HTMLCanvasElement,
        imageSrc: string,
        coordinates: number[][],
        fillColor: string = 'rgba(255, 0, 0, 0.5)',
        strokeColor: string = 'white',
        pointColor: string = 'white',
        background: boolean = false,
        drawLines: boolean = true,
        pointSize: number = 1,
        usePointImage: boolean = false,
    ) => {
        // 获取canvas上下文
        const ctx = canvas.getContext('2d');
        if (!ctx || !coordinates) {
            console.warn('绘制条件不满足');
            return;
        }
        // 创建并加载舌象图片
        const img = document.createElement('img');
        img.src = imageSrc;

        img.onload = () => {
            // 计算画布尺寸和缩放比例
            const displayWidth = canvas.clientWidth;
            const displayHeight = canvas.clientHeight;

            // 设置canvas实际绘制尺寸
            canvas.width = displayWidth;
            canvas.height = displayHeight;

            // 计算图片在画布中的最佳显示尺寸
            const scale = Math.min(
                displayWidth / img.width,
                displayHeight / img.height
            );
            const drawWidth = img.width * scale;
            const drawHeight = img.height * scale;

            // 计算图片居中位置
            const x = (displayWidth - drawWidth) / 2;
            const y = (displayHeight - drawHeight) / 2;

            // 保存当前绘图状态
            ctx.save();

            // 创建圆角矩形路径（50像素圆角）
            const radius = 50;
            ctx.beginPath();

            // 确保圆角不超过图片尺寸的一半
            const actualRadius = Math.min(radius, Math.min(drawWidth, drawHeight) / 2);

            // 绘制圆角矩形
            ctx.moveTo(x + actualRadius, y);
            ctx.lineTo(x + drawWidth - actualRadius, y);
            ctx.quadraticCurveTo(x + drawWidth, y, x + drawWidth, y + actualRadius);
            ctx.lineTo(x + drawWidth, y + drawHeight - actualRadius);
            ctx.quadraticCurveTo(x + drawWidth, y + drawHeight, x + drawWidth - actualRadius, y + drawHeight);
            ctx.lineTo(x + actualRadius, y + drawHeight);
            ctx.quadraticCurveTo(x, y + drawHeight, x, y + drawHeight - actualRadius);
            ctx.lineTo(x, y + actualRadius);
            ctx.quadraticCurveTo(x, y, x + actualRadius, y);
            ctx.closePath();

            // 设置为裁剪区域
            ctx.clip();

            // 背景处理逻辑，根据是否需要背景色决定绘制方式
            if (background) {
                ctx.fillStyle = fillColor;
                ctx.fillRect(x, y, drawWidth, drawHeight);
            } else {
                ctx.drawImage(img, x, y, drawWidth, drawHeight);
            }

            // 恢复绘图状态（取消裁剪区域）
            ctx.restore();
            if (coordinates.length < 2) {
                return;
            }
            // 将原始图像中的坐标点转换为画布上的坐标点
            const transformCoordinate = (point: number[]) => {
                const scaleFactor = 1;
                return [
                    (point[0] / img.width) * drawWidth * scaleFactor +
                    (displayWidth - drawWidth * scaleFactor) / 2,
                    (point[1] / img.height) * drawHeight * scaleFactor +
                    (displayHeight - drawHeight * scaleFactor) / 2
                ];
            };

            // 应用Graham扫描算法获取凸包点
            const transformedPoints = grahamScan(coordinates).map(transformCoordinate);

            // 根据配置决定是否绘制连线
            if (drawLines && transformedPoints.length > 1) {
                ctx.beginPath();
                ctx.moveTo(transformedPoints[0][0], transformedPoints[0][1]);
                transformedPoints.slice(1).forEach(p => ctx.lineTo(p[0], p[1]));
                ctx.closePath();

                if (fillColor !== 'transparent') {
                    ctx.fillStyle = fillColor;
                    ctx.fill();
                }

                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 2;
                ctx.lineJoin = 'round';
                ctx.stroke();
            }

            // 关键点标记逻辑，根据配置决定使用图片还是圆形绘制标记点
            if (usePointImage) {
                // 使用图片描点
                const pointImg = new Image();
                pointImg.onload = () => {
                    transformedPoints.forEach((p) => {
                        // 在点位置绘制图片，居中对齐
                        ctx.drawImage(
                            pointImg,
                            p[0] - pointSize / 2,
                            p[1] - pointSize / 2,
                            pointSize,
                            pointSize
                        );
                    });
                };
                pointImg.src = tonguePoint;
            } else {
                // 使用原来的圆形描点
                transformedPoints.forEach((p) => {
                    ctx.beginPath();
                    ctx.arc(p[0], p[1], pointSize, 0, Math.PI * 2);
                    ctx.fillStyle = pointColor;
                    ctx.fill();
                });
            }
        };

        img.onerror = (e) => console.error('图片加载失败', e);
    };

    // 初始化绘制
    useEffect(() => {
        canvasRefs.current.forEach((canvas, index) => {
            if (!canvas) return;

            const configs: any = [
                {
                    data: record?.resultData.checkResult.relativeOriginalCoordinates,
                    fill: 'transparent',
                    stroke: 'white',
                    point: 'white',
                },
                {
                    data: record?.resultData.checkResult.relativeComparison,
                    fill: 'transparent',
                    stroke: 'white',
                    point: 'white',
                },
                {
                    data: record?.resultData.checkResult.relativeToothMarks,
                    fill: 'transparent',
                    stroke: 'white',
                    point: 'white',
                    drawLines: false,
                    pointSize: 12,
                    usePointImage: true,
                },
                // {
                //     data: record?.resultData.checkResult.relativeComparison,
                //     fill: '#673ab7',
                //     stroke: 'yellow',
                //     point: 'yellow',
                //     background: true,
                // }
            ];

            if (index < configs.length) {
                drawOnCanvas(
                    canvas,
                    record?.resultData.checkResult.cropUrl,
                    configs[index].data,
                    configs[index].fill,
                    configs[index].stroke,
                    configs[index].point,
                    configs[index].background,
                    configs[index]?.drawLines,
                    configs[index]?.pointSize,
                    configs[index]?.usePointImage,
                );
            }
        });
    }, [record]);

    return visible ? (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.8)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}
            onClick={onCancel}
        >
            <div
                style={{
                    width: '80%',
                    maxWidth: 800,
                    padding: 16,
                    position: 'relative'
                }}
                onClick={(e) => { e.stopPropagation() }}
            >
                <Carousel
                    ref={carouselRef}
                    afterChange={current => setActiveIndex(current)}
                    dots={{ className: 'custom-dots' }}
                    effect="fade"
                    style={{
                        overflow: 'hidden'
                    }}
                >
                    {getValue().map((_, index) => (
                        <div key={index} style={{
                            background: index === 3 ? '#673ab7' : 'transparent',
                            minHeight: 400,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                width: '100%',
                                height: 400,
                                position: 'relative',
                            }}>
                                <canvas
                                    ref={el => { canvasRefs.current[index] = el; }}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        zIndex: 1
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </Carousel>
            </div>
            <div className="bottomTitle" style={{
                backgroundImage: `url(${tongueCloud})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                width: '100%',
                height: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 30,
            }}>
                <p style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: 18,
                    color: '#FFE8B1',
                    textAlign: 'center',
                    textTransform: 'none',
                }}>
                    {getValue()[activeIndex].title}
                </p>
            </div>
            <div style={{
                marginTop: 24,
                color: 'white'
            }}>
                {getValue()[activeIndex].value}
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 20
            }}>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleNext();
                    }}
                    style={{
                        padding: '8px 24px',
                        color: 'white',
                        backgroundColor: 'transparent',
                        border: '1px solid #979797',
                        borderRadius: '29px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold',
                    }}
                >
                    下一步
                </button>
            </div>
        </div>
    ) : null;
};

export default TongueCheck;